import { AccountDetailsFormValues } from '@account/types';
import { Select } from '@components/utility/selects';
import { IOSSwitch } from '@components/utility/switches';
import { FALLBACK_LANGUAGES } from '@constants/common';
import { useGetLanguagesQuery } from '@services/translation';
import { renderCountryOption, renderCountryValue } from '@utils/common';
import { FOOD_PREFERENCES } from '@utils/user';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

export const Preferences = ({ loading }: { loading?: boolean }) => {
    //#region Hooks
    const intl = useIntl();
    const foodPreferenceOptions = FOOD_PREFERENCES(intl);
    const { values, errors, touched, setFieldValue, handleChange } = useFormikContext<AccountDetailsFormValues>();
    //#endregion

    //#region Data Fetching
    const { data: languages = FALLBACK_LANGUAGES, isLoading: isLanguagesLoading } = useGetLanguagesQuery();
    //#endregion

    return (
        <>
            <IOSSwitch
                id="receive_mailings_targeted_at_professionals"
                topLabel={
                    <FormattedMessage
                        id="accounts.preferences.receiveMailings"
                        defaultMessage="Receive mailings targeted at professionals?"
                    />
                }
                required
                helperText={
                    touched.receive_mailings_targeted_at_professionals &&
                    errors.receive_mailings_targeted_at_professionals
                        ? errors.receive_mailings_targeted_at_professionals
                        : undefined
                }
                name="receive_mailings_targeted_at_professionals"
                checked={!!values.receive_mailings_targeted_at_professionals}
                onChange={(_e, checked) => setFieldValue('receive_mailings_targeted_at_professionals', Number(checked))}
                formControlProps={{
                    className: 'md:col-span-2',
                    error:
                        touched.receive_mailings_targeted_at_professionals &&
                        Boolean(errors.receive_mailings_targeted_at_professionals),
                }}
                loading={loading}
            />
            <Select
                id="language"
                name="language"
                value={values.language ?? ''}
                onChange={handleChange}
                items={languages}
                type="default"
                renderValue={(value) => renderCountryValue(value as string, languages)}
                size="smallest"
                renderOption={renderCountryOption}
                fullWidth
                boxProps={{
                    className: 'md:col-span-2 w-full',
                }}
                topLabel={intl.formatMessage({ id: 'common.language', defaultMessage: 'Language' })}
                required
                error={touched.language && Boolean(errors.language)}
                helperText={touched.language && errors.language ? errors.language : undefined}
                loading={loading || isLanguagesLoading}
                loadingType="skeleton"
            />
            <Select
                id="food_preference"
                name="food_preference"
                value={values.food_preference ?? ''}
                onChange={handleChange}
                items={FOOD_PREFERENCES(intl)}
                type="default"
                size="smallest"
                fullWidth
                topLabel={intl.formatMessage({ id: 'common.foodPreference', defaultMessage: 'Food Preference' })}
                boxProps={{
                    className: 'md:col-span-2 w-full',
                }}
                error={touched.food_preference && Boolean(errors.food_preference)}
                helperText={
                    touched.food_preference && errors.food_preference
                        ? errors.food_preference
                        : intl.formatMessage({
                              id: 'accounts.preferences.foodPreferenceHelperText',
                              defaultMessage:
                                  "Food preference is used for when you're attending Victron Energy events. Some of the events include lunch / dinner. To satisfy everyone's wish we ask you to set your food preference.",
                          })
                }
                loading={loading}
                loadingType="skeleton"
            />
        </>
    );
};
